/**
 * Sistem Log Servisi — audit trail.
 *
 * Önemli alanlardaki değişiklikleri kim, ne zaman, hangi değerden hangi değere
 * yaptığını kaydeder. Sadece BOSS okuyabilir.
 *
 * Loglar immutable — silinmez, değiştirilmez.
 */

import {
  collection, addDoc, getDocs, query, orderBy, where, limit,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import type { FsTimestamp } from '@/types'

const KOLEKSIYON = 'sistemLog'

export type LogTipi =
  | 'personel.olustur'
  | 'personel.guncelle'
  | 'personel.sil'
  | 'personel.geriYukle'
  | 'maas.hareketEkle'
  | 'maas.hareketSil'
  | 'maas.odemeEkle'
  | 'maas.odemeIptal'
  | 'izin.onayla'
  | 'izin.reddet'
  | 'izin.iptal'
  | 'avans.onayla'
  | 'avans.reddet'
  | 'avans.odendi'
  | 'avans.kararDegistir'
  | 'puantaj.ekle'
  | 'puantaj.sil'
  | 'ayKapanisi.kapat'
  | 'ayKapanisi.ac'
  | 'sube.ekle'
  | 'sube.sil'
  | 'pozisyon.ekle'
  | 'pozisyon.sil'
  | 'ayarlar.degistir'
  | 'diger'

export type LogRol = 'boss' | 'yonetici' | 'personel' | 'sistem'

export interface LogDegisiklik {
  alan: string
  eski: unknown
  yeni: unknown
}

export interface SistemLog {
  id: string
  tip: LogTipi
  baslik: string
  hedefId?: string       // Personel id, vardiya id vb.
  hedefIsim?: string
  yapanId: string
  yapanIsim?: string
  yapanRol: LogRol
  degisiklikler?: LogDegisiklik[]
  ekBilgi?: Record<string, unknown>
  tarih: FsTimestamp
}

export interface YeniLog {
  tip: LogTipi
  baslik: string
  hedefId?: string
  hedefIsim?: string
  yapanId: string
  yapanIsim?: string
  yapanRol: LogRol
  degisiklikler?: LogDegisiklik[]
  ekBilgi?: Record<string, unknown>
}

/**
 * Yeni sistem log kaydı oluşturur.
 * Hata fırlatmaz — log atılamazsa sessizce konsola yazar (asıl işlemi bozmamak için).
 */
export async function logYaz(data: YeniLog): Promise<void> {
  try {
    const db = getFirestoreDb()
    const docData: Record<string, unknown> = {
      tip: data.tip,
      baslik: data.baslik,
      yapanId: data.yapanId,
      yapanRol: data.yapanRol,
      tarih: nowServer(),
    }
    if (data.hedefId) docData.hedefId = data.hedefId
    if (data.hedefIsim) docData.hedefIsim = data.hedefIsim
    if (data.yapanIsim) docData.yapanIsim = data.yapanIsim
    if (data.degisiklikler && data.degisiklikler.length > 0) {
      docData.degisiklikler = data.degisiklikler
    }
    if (data.ekBilgi) docData.ekBilgi = data.ekBilgi

    await addDoc(collection(db, KOLEKSIYON), docData)
  } catch (err) {
    console.error('Log yazılamadı:', err, data)
  }
}

interface LogFiltre {
  tip?: LogTipi | LogTipi[]
  yapanId?: string
  hedefId?: string
  limit?: number
}

export async function loglariGetir(filtre: LogFiltre = {}): Promise<SistemLog[]> {
  const db = getFirestoreDb()
  const filters = []
  if (filtre.tip) {
    if (Array.isArray(filtre.tip)) {
      filters.push(where('tip', 'in', filtre.tip))
    } else {
      filters.push(where('tip', '==', filtre.tip))
    }
  }
  if (filtre.yapanId) filters.push(where('yapanId', '==', filtre.yapanId))
  if (filtre.hedefId) filters.push(where('hedefId', '==', filtre.hedefId))

  const q = query(
    collection(db, KOLEKSIYON),
    ...filters,
    orderBy('tarih', 'desc'),
    limit(filtre.limit ?? 200)
  )
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as SistemLog)
}

/**
 * İki objenin farkını çıkarır. Sadece izlenen alanlarda fark olanlar döner.
 */
export function degisiklikleriHesapla(
  eski: Record<string, unknown>,
  yeni: Record<string, unknown>,
  izlenenAlanlar: string[]
): LogDegisiklik[] {
  const sonuc: LogDegisiklik[] = []
  for (const alan of izlenenAlanlar) {
    const eskiV = eski[alan]
    const yeniV = yeni[alan]
    // Atlas: yeni değer undefined ise (form göndermedi) atla
    if (yeniV === undefined) continue
    // İlkel karşılaştırma (string, number, boolean)
    if (typeof eskiV !== 'object' && typeof yeniV !== 'object') {
      if (eskiV !== yeniV) {
        sonuc.push({ alan, eski: eskiV ?? null, yeni: yeniV ?? null })
      }
      continue
    }
    // Array karşılaştırma (basit)
    if (Array.isArray(eskiV) && Array.isArray(yeniV)) {
      const eskiStr = JSON.stringify([...eskiV].sort())
      const yeniStr = JSON.stringify([...yeniV].sort())
      if (eskiStr !== yeniStr) {
        sonuc.push({ alan, eski: eskiV, yeni: yeniV })
      }
      continue
    }
    // Diğer (object vs object)
    if (JSON.stringify(eskiV) !== JSON.stringify(yeniV)) {
      sonuc.push({ alan, eski: eskiV ?? null, yeni: yeniV ?? null })
    }
  }
  return sonuc
}
